import { useEffect } from 'react';
import { useUserData } from 'flows/core/hooks/use-profile';
import {
  useGetEstimatedActivationDate,
  type ActivationDefaults,
} from 'flows/tvi/hooks/use-get-estimated-activation-date';

import { Address, CreditCard, PiiTokenizedData } from 'flows/tvi/types';
import { useReservation } from 'flows/tvi/hooks/use-reservation';
import { createFormStore } from 'flows/tvi/utils/buy/config/common/form-store';
import {
  HomeServicesReservation,
  InstallationOptionsEnum,
} from '@updater/consumer-graph';
import { SMS_OPT_IN_VALUE } from 'flows/tvi/components/review/utils/constants';

export type ATTFormData = {
  ssn: string | PiiTokenizedData;
  dob: string | PiiTokenizedData;
  passcode: string | PiiTokenizedData;
  securityQuestion: string;
  securityAnswer: string | PiiTokenizedData;
  date1: string;
  date1InstallationWindow: Array<string>;
  date2: string;
  date2InstallationWindow: Array<string>;
  date3: string;
  date3InstallationWindow: Array<string>;
  flexDevices?: string;
  creditCheckMethod: 'autopay' | 'manual';
  email: string;
  firstName: string;
  lastName: string;
  phone: string;
  smsOptIn: 'sms'[];
  currentAddress?: Address;
  creditCard: Partial<CreditCard>;
  shippingAddress?: string;
  newShippingAddress?: Address;
  /*
  This field is not UI visible
  It is used by informant to conditionally render the `Schedule Installation` Screen
  based if the user has self install enabled or not
*/
  installationType: InstallationOptionsEnum;
};

export enum AttPages {
  IDENTITY = 'identity',
  SECURE_ACCOUNT = 'secure-account',
  SCHEDULE_INSTALLATION = 'schedule-installation',
  PAYMENT_OPTION = 'payment-option',
  PAYMENT = 'payment-method',
  REVIEW = 'buy/review',
  SUBMISSION = 'submission',
  ERROR = 'error',
}

export const omitKeysForLogging = [
  'dob',
  'ssn',
  'passcode',
  'securityQuestion',
  'securityAnswer',
  'creditCard',
];

export type UseStoreWrapper = typeof useStore;
const partialFormDataFromReservation = (
  reservation: HomeServicesReservation,
  activationDefaults: ActivationDefaults
) => {
  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;

  // Convert hook's string format to array for AT&T form data
  const defaultInstallWindowTime = activationDefaults.defaultTimeWindow
    ? [activationDefaults.defaultTimeWindow]
    : undefined;

  return {
    installationType:
      installationTypeFromReservation as InstallationOptionsEnum,
    date1InstallationWindow: defaultInstallWindowTime,
  };
};

const useStore = createFormStore<ATTFormData, AttPages>({
  provider: 'att',
  initialPageRoute: AttPages.IDENTITY,
  initialFormData: {},
});

export function getATTFormState() {
  return useStore.getState();
}

export const useATTStore = (...args: Parameters<typeof useStore>) => {
  const status = useStore((state) => state.status);
  const formData = useStore((state) => state.formData);
  const initializeFormData = useStore((state) => state.initializeFormData);
  const { reservation, status: reservationStatus } =
    useReservation('network-only');

  const { loading: userDataLoading, user } = useUserData();
  const activationDefaults = useGetEstimatedActivationDate();

  const hasHydrated = useStore.persist?.hasHydrated();

  const installationTypeFromReservation =
    reservation?.offer?.options?.installationOptions?.type;

  useEffect(() => {
    if (
      hasHydrated &&
      status !== 'loaded' &&
      !userDataLoading &&
      reservationStatus === 'loaded' &&
      reservation &&
      !activationDefaults.loading
    ) {
      const initialData: Partial<ATTFormData> = {
        ...partialFormDataFromReservation(reservation, activationDefaults),
        date1: activationDefaults.estimatedDate ?? '',
        date2: '', // Secondary date (optional)
        date3: '', // Tertiary date (optional)
        creditCheckMethod: 'autopay',
        flexDevices: 'one',
        firstName: user?.firstName ?? undefined,
        lastName: user?.lastName ?? undefined,
        email: user?.email ?? undefined,
        phone: user?.phone ?? undefined,
        smsOptIn: [SMS_OPT_IN_VALUE],
        creditCard: {
          firstName: user?.firstName ?? undefined,
          lastName: user?.lastName ?? undefined,
          address: <Address>{
            street: user?.currentMove?.fromAddress.street ?? undefined,
            unit: user?.currentMove?.fromAddress.unit ?? '',
            city: user?.currentMove?.fromAddress.city ?? undefined,
            state: user?.currentMove?.fromAddress.state ?? undefined,
            postalCode: user?.currentMove?.fromAddress.postalCode ?? undefined,
          },
        },
      };

      initializeFormData({
        formData: initialData,
        offerName: reservation?.offer?.name,
      });
    }
  }, [
    hasHydrated,
    status,
    formData?.installationType,
    userDataLoading,
    user,
    initializeFormData,
    installationTypeFromReservation,
    reservation,
    activationDefaults,
    reservationStatus,
  ]);

  return useStore(...args);
};
